import { httpFrontEndRequest } from '@zg-rentals/ts-client-http-client';
import { useRentalManagerAccountViewZramSingleFamilyV2WebApiHandler_advertising } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import PropertiesConstants from '../../../constants/PropertiesConstants';

import type { QueryClient } from '@zg-rentals/react-query5';
import type { AccountViewListingAdvertisingDto } from '@zg-rentals/ts-rental-manager-api-client';

// Written as a direct read because the generated client does not carry advertisingPage yet. Once it does, the
// request below should collapse into the generated call.
export const SINGLE_FAMILY_ADVERTISING_PAGE_PATH =
  '/api/web/v2/users/accountView/zram/singleFamily/properties/advertisingPage';

// The list page size, so one list page is one read.
const ADVERTISING_PAGE_SIZE = PropertiesConstants.PROPERTIES_LIMIT_DEFAULT;

type AdvertisingPageParams = { listingAliases: string[]; bypassCache?: boolean };
type AdvertisingByListingAlias = Record<string, AccountViewListingAdvertisingDto>;

export const getAdvertisingPageKey = (listingAliases: string[]) =>
  [SINGLE_FAMILY_ADVERTISING_PAGE_PATH, { listingAliases }] as const;

export type AdvertisingPageKey = ReturnType<typeof getAdvertisingPageKey>;

export const isAdvertisingPageKey = (queryKey: unknown): queryKey is AdvertisingPageKey =>
  Array.isArray(queryKey) &&
  queryKey[0] === SINGLE_FAMILY_ADVERTISING_PAGE_PATH &&
  Array.isArray((queryKey[1] as { listingAliases?: unknown } | undefined)?.listingAliases);

// Cut per list page, so a change to one list page (a row removed, a short page) leaves the others' reads alone.
export const chunkAdvertisingPages = (listingAliasPages: string[][]) =>
  listingAliasPages.flatMap((listingAliases) => {
    const pages: string[][] = [];
    for (let start = 0; start < listingAliases.length; start += ADVERTISING_PAGE_SIZE) {
      pages.push(listingAliases.slice(start, start + ADVERTISING_PAGE_SIZE));
    }
    return pages;
  });

// Writes each listing into its own row query, so everything that reads or patches a row keeps using the row key.
// A cancelled read writes nothing, since React Query drops its result. A row written after this read started (an
// optimistic update, a bypass refresh) keeps that newer value. Only rows that already have a query are written,
// since a query created here would have no way to fetch on its own.
export const fetchAdvertisingPage = async (
  queryClient: QueryClient,
  listingAliases: string[],
  { bypassCache = false, signal }: { bypassCache?: boolean; signal?: AbortSignal } = {},
) => {
  const findRowQuery = (listingAlias: string) =>
    queryClient.getQueryCache().find({
      queryKey: useRentalManagerAccountViewZramSingleFamilyV2WebApiHandler_advertising.getKey({ listingAlias }),
      exact: true,
    });
  const rowUpdateCountAtStart = new Map(
    listingAliases.map((listingAlias) => [listingAlias, findRowQuery(listingAlias)?.state.dataUpdateCount ?? 0]),
  );
  const advertisingByListingAlias =
    (await httpFrontEndRequest<AdvertisingPageParams, never, AdvertisingByListingAlias>({
      getParams: bypassCache ? { listingAliases, bypassCache } : { listingAliases },
      endpoint: SINGLE_FAMILY_ADVERTISING_PAGE_PATH,
      method: 'GET',
      contextPath: '/rental-manager-api',
    })) ?? {};
  if (signal?.aborted) {
    return advertisingByListingAlias;
  }
  Object.entries(advertisingByListingAlias).forEach(([listingAlias, advertising]) => {
    const rowQuery = findRowQuery(listingAlias);
    if (rowQuery && rowQuery.state.dataUpdateCount <= (rowUpdateCountAtStart.get(listingAlias) ?? 0)) {
      queryClient.setQueryData(rowQuery.queryKey, advertising);
    }
  });
  return advertisingByListingAlias;
};

// A row that mounts after its page resolved has no row query for the page to write into, so it starts from the page.
// Several cached pages can hold the same row after the list changes, so the most recently read one wins.
export const findLatestPageReadOfRow = (queryClient: QueryClient, listingAlias: string) =>
  queryClient
    .getQueryCache()
    .findAll({
      predicate: (query) =>
        isAdvertisingPageKey(query.queryKey) &&
        query.queryKey[1].listingAliases.includes(listingAlias) &&
        (query.state.data as AdvertisingByListingAlias | undefined)?.[listingAlias] !== undefined,
    })
    .map((query) => ({
      advertising: (query.state.data as AdvertisingByListingAlias)[listingAlias],
      updatedAt: query.state.dataUpdatedAt,
    }))
    .sort((left, right) => right.updatedAt - left.updatedAt)[0];
